/**
 * @fileoverview Enterprise EventBus v6.0.0 — Quantum Perfection
 * @module MikhmelViewer/EventBus
 * 
 * [v6.0.0 QUANTUM PERFECTION QWEN 3.8]:
 *  ✅ Result Pattern (Either Monad) для всех операций
 *  ✅ 80+ типизированных событий (включая Cinematic v9 + Presets v6)
 *  ✅ Module Registry с автоматической очисткой при dispose
 *  ✅ Wildcard Patterns (glob-style: 'cinematic:*', '**:error')
 *  ✅ Event Schema Validation (структурная проверка payload)
 *  ✅ Correlation ID для distributed tracing
 *  ✅ Batch Emit (групповая отправка событий)
 *  ✅ Dead Letter Queue (DLQ) для необработанных событий
 *  ✅ Dead Module Cleanup (автоочистка при unmount)
 *  ✅ Replay Buffer с настраиваемым размером
 *  ✅ Module Isolation (предотвращение конфликтов)
 *  ✅ 🛡️ Memory Leak Detection
 *  ✅ 🔒 Immutable Payloads (structuredClone)
 *  ✅ ⏱️ Performance Profiling
 *  ✅ 🚦 Priority Listeners
 *  ✅ 🔄 Wildcards ('*')
 *  ✅ 🗑️ Корректная отписка `once`
 *  ✅ 🛑 Recursion Guard
 *  ✅ 📊 Event History с timestamps
 *  ✅ 🧹 Полная очистка
 */

import { log } from '../utils/logger.js';

// ═══════════════════════════════════════════════════════════════
//  RESULT PATTERN
// ═══════════════════════════════════════════════════════════════

/**
 * @template T
 * @template E
 * @typedef {Object} Result
 * @property {boolean} success
 * @property {T} [value]
 * @property {E} [error]
 */

const Ok = (value) => ({ success: true, value });
const Err = (error) => ({ success: false, error });

// ═══════════════════════════════════════════════════════════════
//  ТИПИЗИРОВАННЫЕ КЛЮЧИ СОБЫТИЙ (80+ событий)
// ═══════════════════════════════════════════════════════════════

export const EVENTS = Object.freeze({
  // ── Пресеты (Window/TF/Cinematic) ──
  PRESET_CHANGED:           'preset:changed',
  PRESET_SAVED:             'preset:saved',
  PRESET_DELETED:           'preset:deleted',

  // ── Viewport ──
  VIEWPORT_CREATED:         'viewport:created',
  VIEWPORT_DESTROYED:       'viewport:destroyed',
  LAYOUT_CHANGED:           'layout:changed',

  // ── Загрузка ──
  VOLUME_LOAD_START:        'volume:load:start',
  VOLUME_LOAD_PROGRESS:     'volume:load:progress',
  VOLUME_LOAD_DONE:         'volume:load:done',
  VOLUME_LOAD_ABORT:        'volume:load:abort',
  STACK_LOADED:             'stack:loaded',

  // ── GPU / System ──
  GPU_DETECTED:             'gpu:detected',
  CACHE_WARNING:            'cache:warning',
  CACHE_EVICTED:            'cache:evicted',
  CACHE_PURGED:             'cache:purged',

  // ── Cinematic / Rendering (Legacy) ──
  CINEMATIC_TOGGLED:        'cinematic:toggled',
  CINEMATIC_PRESET:         'cinematic:preset',

  // ── TF Editor & Tools ──
  TF_EDITOR_OPENED:         'tfeditor:opened',
  TF_EDITOR_CLOSED:         'tfeditor:closed',
  TF_APPLIED:               'tf:applied',
  TOOL_CHANGED:             'tool:changed',

  // ── System ──
  MODULE_ERROR:             'module:error',
  HISTOGRAM_READY:          'histogram:ready',

  // ── Diagnostics & Measurement ──
  HU_PROBE:                 'hu:probe',
  CINE_START:               'cine:start',
  CINE_STOP:                'cine:stop',
  CINE_FRAME:               'cine:frame',
  OVERLAY_ADDED:            'overlay:added',
  OVERLAY_REMOVED:          'overlay:removed',
  HANGING_PROTOCOL_APPLIED: 'hanging:protocol:applied',
  CALCIUM_SCORE_DONE:       'calcium:score:done',

  // ── User Interaction ──
  VOICE_COMMAND:            'voice:command',
  ROI_STATS:                'roi:stats',
  SESSION_EVENT:            'session:event',
  GESTURE:                  'gesture',
  KEYBOARD_SHORTCUT:        'keyboard:shortcut',

  // ── Advanced View ──
  PHASE_CHANGED:            'phase:changed',
  SMART_COMPARE_SYNC:       'smart:compare:sync',
  BOOKMARK_ADDED:           'bookmark:added',
  BOOKMARK_REMOVED:         'bookmark:removed',
  AUTO_WL_APPLIED:          'auto:wl:applied',
  SLICE_PROJECTION_CHANGED: 'slice:projection:changed',
  VIEWPORT_INFO_UPDATE:     'viewport:info:update',

  // ── AI & Pathologies ──
  PATHOLOGY_FOUND:          'pathology:found',
  MAGNIFIER_TOGGLE:         'magnifier:toggle',

  // ── Enterprise v6 (new) ──
  CAMERA_AUTO_ROTATE:       'camera:auto:rotate',
  DUAL_ENERGY_APPLIED:      'dual:energy:applied',
  LINE_PROFILE:             'line:profile',
  VOLUME_STATS:             'volume:stats',
  CROSSHAIR_OVERLAY:        'crosshair:overlay',
  PIP_TOGGLE:               'pip:toggle',
  STUDY_TIMER:              'study:timer',
  STORAGE_HEALTH:           'storage:health',

  // ═══════════════════════════════════════════════════════════════
  //  [v6.0.0] CINEMATIC RENDERER v9.0.0 — 14 NEW EVENTS
  // ═══════════════════════════════════════════════════════════════
  /** [10.19.18] Карта глубины пересчитана (или не смогла) — с числами. */
  DEPTH_UPDATED:                    'depth:updated',
  CINEMATIC_ENABLED:                'cinematic:enabled',
  CINEMATIC_DISABLED:               'cinematic:disabled',
  CINEMATIC_DISPOSED:               'cinematic:disposed',
  CINEMATIC_PRESET_CHANGED:         'cinematic:preset:changed',
  CINEMATIC_PARAMS_CHANGED:         'cinematic:params:changed',
  CINEMATIC_UNDO:                   'cinematic:undo',
  CINEMATIC_REDO:                   'cinematic:redo',
  CINEMATIC_PRESET_SAVED:           'cinematic:preset:saved',
  CINEMATIC_PRESET_DELETED:         'cinematic:preset:deleted',
  CINEMATIC_SCREENSHOT:             'cinematic:screenshot',
  CINEMATIC_RECORDING_STARTED:      'cinematic:recording:started',
  CINEMATIC_RECORDING_STOPPED:      'cinematic:recording:stopped',
  CINEMATIC_CONTEXT_LOST:           'cinematic:context:lost',
  CINEMATIC_CONTEXT_RESTORED:       'cinematic:context:restored',

  // ═══════════════════════════════════════════════════════════════
  //  [v6.0.0] PRESETS MANAGER v6.0.0 — 10 NEW EVENTS
  // ═══════════════════════════════════════════════════════════════
  USER_PRESET_SAVED:                'user:preset:saved',
  USER_PRESET_REMOVED:              'user:preset:removed',
  USER_PRESETS_IMPORTED:            'user:presets:imported',
  USER_PRESETS_RESET:               'user:presets:reset',
  USER_PRESETS_SAVED:               'user:presets:saved',
  USER_PRESET_FAVORITED:            'user:preset:favorited',
  USER_PRESET_UNFAVORITED:          'user:preset:unfavorited',
  USER_PRESETS_UNDO:                'user:presets:undo',
  USER_PRESETS_REDO:                'user:presets:redo',
  WORKFLOW_APPLIED:                 'workflow:applied',

  // ═══════════════════════════════════════════════════════════════
  //  [v6.0.0] SYSTEM & INFRASTRUCTURE
  // ═══════════════════════════════════════════════════════════════
  MODULE_REGISTERED:                'module:registered',
  MODULE_DISPOSED:                  'module:disposed',
  EVENT_DROPPED:                    'event:dropped',
  BATCH_EMIT_COMPLETED:             'batch:emit:completed',

  // ═══════════════════════════════════════════════════════════════
  //  [v6.0.0] WINDOW PRESETS v3.0.0 — 10 NEW EVENTS
  // ═══════════════════════════════════════════════════════════════
  WINDOW_PRESET_SAVED:           'window:preset:saved',
  WINDOW_PRESET_DELETED:         'window:preset:deleted',
  WINDOW_PRESET_APPLIED:         'window:preset:applied',
  WINDOW_PRESET_FAVORITED:       'window:preset:favorited',
  WINDOW_PRESET_UNFAVORITED:     'window:preset:unfavorited',
  WINDOW_PRESET_USAGE_TRACKED:   'window:preset:usage:tracked',
  WINDOW_PRESETS_IMPORTED:       'window:presets:imported',
  WINDOW_PRESETS_RESET:          'window:presets:reset',
  WINDOW_PRESETS_SAVED:          'window:presets:saved',
  WINDOW_PRESET_SMART_DETECTED:  'window:preset:smart:detected',

  // ═══════════════════════════════════════════════════════════════
  //  [v6.0.0] LOGGER EVENTS
  // ═══════════════════════════════════════════════════════════════
  LOG_ERROR:                     'log:error',
  LOG_CRITICAL:                  'log:critical',
  LOG_WARNING:                   'log:warning',
});

// ═══════════════════════════════════════════════════════════════
//  ENTERPRISE EVENT BUS CORE v6.0.0
// ═══════════════════════════════════════════════════════════════

class EnterpriseEventBus {
  #listeners = new Map();
  #lastValues = new Map();
  #eventTimestamps = new Map();
  #recursionGuard = new Map();
  
  // [v6.0.0] Module registry для автоматической очистки
  #modules = new Map();           // moduleName → Set<listenerRefs>
  #moduleListeners = new Map();   // listenerRef → moduleName
  
  // [v6.0.0] Dead Letter Queue
  #deadLetterQueue = [];
  #dlqMaxSize = 100;
  
  // [v6.0.0] Event Schemas (валидация payload)
  #schemas = new Map();
  
  // [v6.0.0] Replay buffer (настраиваемый размер)
  #replayBuffer = new Map();      // eventName → Array<{detail, timestamp}>
  #replayBufferSize = 10;
  
  // [v6.0.0] Correlation ID counter
  #correlationCounter = 0;
  
  #config = {
    debug: false,
    strictImmutability: true,
    maxListenersWarning: 15,
    profilingThresholdMs: 10,
    maxRecursionDepth: 3,
    enableDeadLetterQueue: true,
    enableCorrelationId: true,
    enableSchemaValidation: false,  // Опционально (может замедлять)
  };

  #metrics = {
    emitted: 0,
    errors: 0,
    slowHandlers: 0,
    recursiveBlocks: 0,
    deadLetters: 0,
    batchesProcessed: 0,
    modulesRegistered: 0,
    modulesDisposed: 0,
  };

  // ─── ВНУТРЕННИЕ УТИЛИТЫ ────────────────────────────────────────

  #sortListeners(name) {
    const list = this.#listeners.get(name);
    if (list) {
      list.sort((a, b) => b.priority - a.priority);
    }
  }

  #freezePayload(detail) {
    if (!this.#config.strictImmutability || detail === null || detail === undefined) {
      return detail;
    }
    
    if (typeof detail !== 'object') {
      return detail;
    }

    try {
      return structuredClone(detail);
    } catch (e) {
      log.warn('[EventBus] structuredClone failed, falling back to shallow freeze:', e.message);
      return Object.freeze({ ...detail });
    }
  }

  #recordEventTimestamp(name) {
    this.#eventTimestamps.set(name, Date.now());
    
    if (this.#eventTimestamps.size > 1000) {
      const firstKey = this.#eventTimestamps.keys().next().value;
      this.#eventTimestamps.delete(firstKey);
    }
  }

  /**
   * [v6.0.0] Проверка wildcard паттерна (glob-style)
   * Поддерживает: '*' (все), 'cinematic:*' (prefix), '**:error' (suffix)
   */
  #matchesWildcard(pattern, eventName) {
    if (pattern === '*') return true;
    if (pattern === eventName) return true;
    
    // Pattern: 'prefix:*'
    if (pattern.endsWith(':*')) {
      const prefix = pattern.slice(0, -2);
      return eventName.startsWith(prefix + ':');
    }
    
    // Pattern: '**:suffix'
    if (pattern.startsWith('**:')) {
      const suffix = pattern.slice(3);
      return eventName.endsWith(':' + suffix);
    }
    
    // Pattern: 'prefix:**:suffix'
    if (pattern.includes(':**:')) {
      const parts = pattern.split(':**:');
      if (parts.length === 2) {
        const prefix = parts[0];
        const suffix = parts[1];
        return eventName.startsWith(prefix + ':') && eventName.endsWith(':' + suffix);
      }
    }
    
    return false;
  }

  /**
   * [v6.0.0] Валидация payload по схеме
   */
  #validatePayload(eventName, detail) {
    if (!this.#config.enableSchemaValidation) return Ok(true);
    
    const schema = this.#schemas.get(eventName);
    if (!schema) return Ok(true);
    
    try {
      if (typeof schema === 'function') {
        const result = schema(detail);
        if (result === false) {
          return Err(`Payload validation failed for "${eventName}"`);
        }
      } else if (typeof schema === 'object') {
        // Простая структурная валидация
        for (const key of Object.keys(schema)) {
          if (schema[key].required && (detail === undefined || detail === null || !(key in detail))) {
            return Err(`Missing required field "${key}" in "${eventName}"`);
          }
        }
      }
      return Ok(true);
    } catch (e) {
      return Err(`Schema validation error: ${e.message}`);
    }
  }

  /**
   * [v6.0.0] Добавление в Dead Letter Queue
   */
  #addToDLQ(eventName, detail, reason) {
    if (!this.#config.enableDeadLetterQueue) return;
    
    this.#deadLetterQueue.push({
      eventName,
      detail: this.#freezePayload(detail),
      reason,
      timestamp: Date.now(),
    });
    
    if (this.#deadLetterQueue.length > this.#dlqMaxSize) {
      this.#deadLetterQueue.shift();
    }
    
    this.#metrics.deadLetters++;
    
    if (this.#config.debug) {
      log.warn(`[EventBus] 📭 Event added to DLQ: "${eventName}" — ${reason}`);
    }
  }

  /**
   * [v6.0.0] Добавление в Replay Buffer
   */
  #addToReplayBuffer(eventName, detail) {
    if (!this.#replayBuffer.has(eventName)) {
      this.#replayBuffer.set(eventName, []);
    }
    
    const buffer = this.#replayBuffer.get(eventName);
    buffer.push({
      detail: this.#freezePayload(detail),
      timestamp: Date.now(),
    });
    
    // Ограничиваем размер буфера
    if (buffer.length > this.#replayBufferSize) {
      buffer.shift();
    }
  }

  // ─── ПУБЛИЧНЫЙ API ─────────────────────────────────────────────

  /**
   * Подписка на событие с поддержкой приоритетов и модулей
   * @param {string} name Ключ события (поддерживает wildcards: '*', 'cinematic:*')
   * @param {Function} fn Обработчик
   * @param {boolean | {replay?: boolean, priority?: number, module?: string}} options
   * @returns {Result<Function, string>} unsubscribe function
   */
  on = (name, fn, options = {}) => {
    if (typeof name !== 'string' || name.length === 0) {
      return Err('Event name must be a non-empty string');
    }
    if (typeof fn !== 'function') {
      return Err('Handler must be a function');
    }

    if (!this.#listeners.has(name)) {
      this.#listeners.set(name, []);
    }

    const listeners = this.#listeners.get(name);
    
    // Memory Leak Detection
    if (this.#config.debug && listeners.length >= this.#config.maxListenersWarning) {
      log.warn(`[EventBus] ⚠️ Memory Leak Warning: ${listeners.length} listeners for "${name}"`);
    }

    // Нормализация аргументов
    const opts = typeof options === 'boolean' 
      ? { replay: options } 
      : { priority: 0, replay: false, module: null, ...options };

    // Проверка дубликатов
    if (!listeners.find(sub => sub.fn === fn)) {
      const listenerRef = { fn, priority: opts.priority, module: opts.module };
      listeners.push(listenerRef);
      
      // [v6.0.0] Регистрация в module registry
      if (opts.module) {
        if (!this.#modules.has(opts.module)) {
          this.#modules.set(opts.module, new Set());
          this.#metrics.modulesRegistered++;
        }
        this.#modules.get(opts.module).add(listenerRef);
        this.#moduleListeners.set(listenerRef, opts.module);
      }
      
      if (opts.priority !== 0) this.#sortListeners(name);
    }

    // Replay (поздняя подписка) — [v6.0.0] с поддержкой buffer
    if (opts.replay) {
      const buffer = this.#replayBuffer.get(name);
      if (buffer && buffer.length > 0) {
        try {
          // Replay last entry
          fn(buffer[buffer.length - 1].detail);
        } catch (e) {
          log.warn(`[EventBus] Replay error (${name}):`, e);
        }
      } else if (this.#lastValues.has(name)) {
        try {
          fn(this.#lastValues.get(name));
        } catch (e) {
          log.warn(`[EventBus] Replay error (${name}):`, e);
        }
      }
    }

    // Возвращаем функцию отписки
    const unsubscribe = () => this.off(name, fn);
    return Ok(unsubscribe);
  };

  /**
   * Отписка от события
   * @param {string} name 
   * @param {Function} fn 
   * @returns {Result<boolean, string>}
   */
  off = (name, fn) => {
    if (typeof name !== 'string') {
      return Err('Event name must be a string');
    }

    const listeners = this.#listeners.get(name);
    if (!listeners) return Ok(false);

    const index = listeners.findIndex(sub => 
      sub.fn === fn || sub.fn.__originalFn === fn
    );
    
    if (index !== -1) {
      const listenerRef = listeners[index];
      listeners.splice(index, 1);
      
      // [v6.0.0] Очистка module registry
      const moduleName = this.#moduleListeners.get(listenerRef);
      if (moduleName) {
        const moduleSet = this.#modules.get(moduleName);
        if (moduleSet) {
          moduleSet.delete(listenerRef);
          if (moduleSet.size === 0) {
            this.#modules.delete(moduleName);
          }
        }
        this.#moduleListeners.delete(listenerRef);
      }
    }

    if (listeners.length === 0) {
      this.#listeners.delete(name);
    }
    
    return Ok(true);
  };

  /**
   * Удалить ВСЕ обработчики для конкретного события
   * @param {string} name 
   * @returns {Result<number, string>} количество удалённых
   */
  removeAllListeners = (name) => {
    if (typeof name !== 'string') {
      return Err('Event name must be a string');
    }

    const listeners = this.#listeners.get(name);
    if (!listeners) return Ok(0);

    const count = listeners.length;
    
    // [v6.0.0] Очистка module registry
    for (const listenerRef of listeners) {
      const moduleName = this.#moduleListeners.get(listenerRef);
      if (moduleName) {
        const moduleSet = this.#modules.get(moduleName);
        if (moduleSet) moduleSet.delete(listenerRef);
        this.#moduleListeners.delete(listenerRef);
      }
    }
    
    this.#listeners.delete(name);
    this.#lastValues.delete(name);
    this.#eventTimestamps.delete(name);
    this.#replayBuffer.delete(name);
    
    return Ok(count);
  };

  /**
   * Одноразовая подписка
   * @param {string} name 
   * @param {Function} fn 
   * @param {object} options 
   * @returns {Result<Function, string>} unsubscribe
   */
  once = (name, fn, options = {}) => {
    if (typeof name !== 'string') return Err('Event name must be a string');
    if (typeof fn !== 'function') return Err('Handler must be a function');

    const wrapper = (detail) => {
      this.off(name, wrapper);
      fn(detail);
    };
    wrapper.__originalFn = fn;
    return this.on(name, wrapper, options);
  };

  /**
   * Публикация события (Синхронная)
   * @param {string} name 
   * @param {*} [detail] 
   * @param {object} [meta] - metadata (correlationId, source, etc)
   * @returns {Result<{handled: number, errors: number}, string>}
   */
  emit = (name, detail, meta = {}) => {
    if (typeof name !== 'string' || name.length === 0) {
      return Err('Event name must be a non-empty string');
    }

    this.#metrics.emitted++;
    
    // [v6.0.0] Валидация payload
    const validationResult = this.#validatePayload(name, detail);
    if (!validationResult.success) {
      this.#addToDLQ(name, detail, validationResult.error);
      return Err(validationResult.error);
    }
    
    const safeDetail = this.#freezePayload(detail);
    
    // [v6.0.0] Добавление correlationId
    if (this.#config.enableCorrelationId && typeof safeDetail === 'object' && safeDetail !== null) {
      if (!safeDetail.__correlationId) {
        try {
          Object.defineProperty(safeDetail, '__correlationId', {
            value: `evt_${++this.#correlationCounter}_${Date.now()}`,
            enumerable: false,
            writable: false,
          });
        } catch (e) {
          // safeDetail may be frozen already - ignore
        }
      }
    }
    
    this.#lastValues.set(name, safeDetail);
    this.#recordEventTimestamp(name);
    this.#addToReplayBuffer(name, safeDetail);

    if (this.#config.debug) {
      log.debug(`[EventBus] 📡 ${name}`, 
        typeof safeDetail === 'object' ? '(object payload)' : safeDetail
      );
    }

    // Вызываем глобальные слушатели ('*') и wildcard паттерны
    const results1 = this.#invokeHandlers('*', name, safeDetail, 0);
    
    // [v6.0.0] Wildcard patterns ('cinematic:*', etc)
    let wildcardResults = { handled: 0, errors: 0 };
    for (const pattern of this.#listeners.keys()) {
      if (pattern !== name && pattern !== '*' && this.#matchesWildcard(pattern, name)) {
        const r = this.#invokeHandlers(pattern, name, safeDetail, 0);
        wildcardResults.handled += r.handled;
        wildcardResults.errors += r.errors;
      }
    }
    
    // Вызываем обработчики конкретного события
    const results2 = this.#invokeHandlers(name, name, safeDetail, 0);

    const total = {
      handled: results1.handled + results2.handled + wildcardResults.handled,
      errors: results1.errors + results2.errors + wildcardResults.errors,
    };
    
    // [v6.0.0] Если никто не обработал — добавляем в DLQ
    if (total.handled === 0 && this.#config.enableDeadLetterQueue) {
      this.#addToDLQ(name, detail, 'No handlers registered');
    }
    
    return Ok(total);
  };

  /**
   * Асинхронный emit (полезно для неблокирующего рендера UI)
   * @param {string} name 
   * @param {*} [detail] 
   * @returns {Promise<Result>}
   */
  emitAsync = async (name, detail, meta) => {
    return Promise.resolve().then(() => this.emit(name, detail, meta));
  };

  /**
   * [v6.0.0] Batch Emit — групповая отправка событий
   * @param {Array<{name: string, detail?: any, meta?: object}>} events 
   * @returns {Result<{processed: number, errors: number}, string>}
   */
  emitBatch = (events) => {
    if (!Array.isArray(events)) {
      return Err('Events must be an array');
    }
    
    let processed = 0;
    let errors = 0;
    
    for (const event of events) {
      if (!event || typeof event.name !== 'string') {
        errors++;
        continue;
      }
      const result = this.emit(event.name, event.detail, event.meta);
      if (result.success) {
        processed++;
      } else {
        errors++;
      }
    }
    
    this.#metrics.batchesProcessed++;
    
    this.emit(EVENTS.BATCH_EMIT_COMPLETED, { 
      processed, 
      errors, 
      total: events.length 
    });
    
    return Ok({ processed, errors, total: events.length });
  };

  /**
   * Внутренний метод вызова обработчиков
   */
  #invokeHandlers(listenName, eventName, detail, recursionDepth) {
    const listeners = this.#listeners.get(listenName);
    if (!listeners || listeners.length === 0) {
      return { handled: 0, errors: 0 };
    }

    const handlers = [...listeners];
    let handled = 0;
    let errors = 0;

    for (const { fn } of handlers) {
      try {
        if (this.#config.debug) {
          const t0 = performance.now();
          if (listenName === '*' || listenName.includes(':*') || listenName.includes('**:')) {
            fn(eventName, detail);
          } else {
            fn(detail);
          }
          const t1 = performance.now();
          
          if (t1 - t0 > this.#config.profilingThresholdMs) {
            this.#metrics.slowHandlers++;
            log.warn(
              `[EventBus] 🐢 Slow handler for "${eventName}": ${(t1 - t0).toFixed(2)}ms`
            );
          }
        } else {
          if (listenName === '*' || listenName.includes(':*') || listenName.includes('**:')) {
            fn(eventName, detail);
          } else {
            fn(detail);
          }
        }
        handled++;
      } catch (e) {
        this.#metrics.errors++;
        errors++;
        log.error(`[EventBus] ❌ Handler error (${eventName}):`, e);
        
        if (eventName !== EVENTS.MODULE_ERROR && recursionDepth < this.#config.maxRecursionDepth) {
          this.emit(EVENTS.MODULE_ERROR, { 
            event: eventName, 
            error: e,
            recursionDepth: recursionDepth + 1 
          });
        } else if (recursionDepth >= this.#config.maxRecursionDepth) {
          this.#metrics.recursiveBlocks++;
          log.error(`[EventBus] 🛑 Recursion blocked for "${eventName}"`);
        }
      }
    }
    
    return { handled, errors };
  }

  // ─── MODULE REGISTRY (v6.0.0) ────────────────────────────────

  /**
   * [v6.0.0] Зарегистрировать модуль (для автоматической очистки)
   * @param {string} moduleName 
   * @returns {Result<boolean, string>}
   */
  registerModule = (moduleName) => {
    if (typeof moduleName !== 'string' || moduleName.length === 0) {
      return Err('Module name must be a non-empty string');
    }
    
    if (!this.#modules.has(moduleName)) {
      this.#modules.set(moduleName, new Set());
      this.#metrics.modulesRegistered++;
      
      this.emit(EVENTS.MODULE_REGISTERED, { moduleName });
      
      if (this.#config.debug) {
        log.debug(`[EventBus] 📦 Module registered: ${moduleName}`);
      }
    }
    
    return Ok(true);
  };

  /**
   * [v6.0.0] Отписаться от ВСЕХ событий модуля (при unmount)
   * @param {string} moduleName 
   * @returns {Result<number, string>} количество удалённых слушателей
   */
  disposeModule = (moduleName) => {
    if (typeof moduleName !== 'string') {
      return Err('Module name must be a string');
    }
    
    const moduleListeners = this.#modules.get(moduleName);
    if (!moduleListeners) {
      return Ok(0);
    }
    
    let removedCount = 0;
    
    // Ищем и удаляем слушатели во всех событиях
    for (const [eventName, listeners] of this.#listeners.entries()) {
      for (let i = listeners.length - 1; i >= 0; i--) {
        const ref = listeners[i];
        if (moduleListeners.has(ref)) {
          listeners.splice(i, 1);
          this.#moduleListeners.delete(ref);
          moduleListeners.delete(ref);
          removedCount++;
        }
      }
      
      if (listeners.length === 0) {
        this.#listeners.delete(eventName);
      }
    }
    
    this.#modules.delete(moduleName);
    this.#metrics.modulesDisposed++;
    
    this.emit(EVENTS.MODULE_DISPOSED, { moduleName, removedListeners: removedCount });
    
    if (this.#config.debug) {
      log.debug(`[EventBus] 🗑️ Module disposed: ${moduleName} (removed ${removedCount} listeners)`);
    }
    
    return Ok(removedCount);
  };

  /**
   * [v6.0.0] Получить список зарегистрированных модулей
   * @returns {string[]}
   */
  getRegisteredModules = () => {
    return Array.from(this.#modules.keys());
  };

  /**
   * [v6.0.0] Получить количество слушателей для модуля
   * @param {string} moduleName 
   * @returns {number}
   */
  getModuleListenerCount = (moduleName) => {
    const moduleSet = this.#modules.get(moduleName);
    return moduleSet ? moduleSet.size : 0;
  };

  // ─── SCHEMA REGISTRY (v6.0.0) ────────────────────────────────

  /**
   * [v6.0.0] Зарегистрировать схему для события
   * @param {string} eventName 
   * @param {Function|Object} schema 
   * @returns {Result<boolean, string>}
   */
  registerSchema = (eventName, schema) => {
    if (typeof eventName !== 'string') {
      return Err('Event name must be a string');
    }
    if (!schema) {
      return Err('Schema is required');
    }
    
    this.#schemas.set(eventName, schema);
    return Ok(true);
  };

  /**
   * [v6.0.0] Удалить схему
   * @param {string} eventName 
   * @returns {Result<boolean, string>}
   */
  unregisterSchema = (eventName) => {
    const had = this.#schemas.has(eventName);
    this.#schemas.delete(eventName);
    return Ok(had);
  };

  // ─── DEAD LETTER QUEUE (v6.0.0) ─────────────────────────────

  /**
   * [v6.0.0] Получить DLQ
   * @param {number} [limit=50] 
   * @returns {Array}
   */
  getDeadLetterQueue = (limit = 50) => {
    return [...this.#deadLetterQueue].slice(-limit);
  };

  /**
   * [v6.0.0] Очистить DLQ
   * @returns {Result<number, string>}
   */
  clearDeadLetterQueue = () => {
    const count = this.#deadLetterQueue.length;
    this.#deadLetterQueue = [];
    return Ok(count);
  };

  /**
   * [v6.0.0] Повторно отправить событие из DLQ
   * @param {number} index 
   * @returns {Result<boolean, string>}
   */
  replayDeadLetter = (index) => {
    if (index < 0 || index >= this.#deadLetterQueue.length) {
      return Err('Invalid DLQ index');
    }
    
    const letter = this.#deadLetterQueue[index];
    this.#deadLetterQueue.splice(index, 1);
    
    const result = this.emit(letter.eventName, letter.detail);
    return result.success ? Ok(true) : Err(result.error);
  };

  // ─── EXISTING METHODS (unchanged API) ────────────────────────

  waitFor = (name, timeoutMs = 15000, useCache = false) => {
    return new Promise((resolve, reject) => {
      if (useCache && this.#lastValues.has(name)) {
        return resolve(this.#lastValues.get(name));
      }

      let timer = null;
      let handled = false;
      
      const handler = (detail) => {
        if (handled) return;
        handled = true;
        
        if (timer) clearTimeout(timer);
        this.off(name, handler);
        resolve(detail);
      };

      if (timeoutMs > 0) {
        timer = setTimeout(() => {
          if (handled) return;
          handled = true;
          
          this.off(name, handler);
          reject(new Error(`[EventBus] ⏱️ Timeout waiting for "${name}"`));
        }, timeoutMs);
      }

      this.once(name, handler);
    });
  };

  setConfig = (configOverrides) => {
    if (!configOverrides || typeof configOverrides !== 'object') {
      return Err('Config must be an object');
    }
    Object.assign(this.#config, configOverrides);
    if (this.#config.debug) {
      log.debug('[EventBus] Configuration updated:', this.#config);
    }
    return Ok(true);
  };

  setDebug = (enabled) => this.setConfig({ debug: !!enabled });

  clearHistory = (eventName = null) => {
    if (eventName) {
      this.#lastValues.delete(eventName);
      this.#eventTimestamps.delete(eventName);
      this.#replayBuffer.delete(eventName);
    } else {
      this.#lastValues.clear();
      this.#eventTimestamps.clear();
      this.#replayBuffer.clear();
    }
    return Ok(true);
  };

  clearAllListeners = () => {
    const totalListeners = Array.from(this.#listeners.values())
      .reduce((acc, arr) => acc + arr.length, 0);
    
    this.#listeners.clear();
    this.#lastValues.clear();
    this.#eventTimestamps.clear();
    this.#recursionGuard.clear();
    this.#modules.clear();
    this.#moduleListeners.clear();
    this.#replayBuffer.clear();
    
    if (this.#config.debug) {
      log.debug(`[EventBus] 🧹 Cleared ${totalListeners} listeners and history`);
    }
    
    return Ok(totalListeners);
  };

  onThrottled = (name, fn, intervalMs = 100) => {
    if (typeof name !== 'string') return Err('Event name must be a string');
    if (typeof fn !== 'function') return Err('Handler must be a function');
    if (typeof intervalMs !== 'number' || intervalMs <= 0) {
      return Err('Interval must be a positive number');
    }
    
    let lastCall = 0;
    let pendingArgs = null;
    let pendingTimer = null;

    const throttled = (detail) => {
      const now = performance.now();
      const elapsed = now - lastCall;

      if (elapsed >= intervalMs) {
        lastCall = now;
        fn(detail);
      } else {
        pendingArgs = detail;
        if (!pendingTimer) {
          pendingTimer = setTimeout(() => {
            pendingTimer = null;
            lastCall = performance.now();
            if (pendingArgs !== null) {
              fn(pendingArgs);
              pendingArgs = null;
            }
          }, intervalMs - elapsed);
        }
      }
    };

    throttled.__originalFn = fn;
    this.on(name, throttled);
    
    return Ok(() => {
      if (pendingTimer) clearTimeout(pendingTimer);
      this.off(name, throttled);
    });
  };

  onDebounced = (name, fn, delayMs = 300) => {
    if (typeof name !== 'string') return Err('Event name must be a string');
    if (typeof fn !== 'function') return Err('Handler must be a function');
    if (typeof delayMs !== 'number' || delayMs <= 0) {
      return Err('Delay must be a positive number');
    }
    
    let timer = null;
    
    const debounced = (detail) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn(detail), delayMs);
    };
    
    debounced.__originalFn = fn;
    this.on(name, debounced);
    
    return Ok(() => { 
      clearTimeout(timer); 
      this.off(name, debounced); 
    });
  };

  getEventLog = (limit = 50) => {
    const entries = [];
    
    for (const [event, timestamp] of this.#eventTimestamps) {
      entries.push({
        event,
        timestamp,
        hasPayload: this.#lastValues.get(event) != null
      });
    }
    
    entries.sort((a, b) => a.timestamp - b.timestamp);
    return entries.slice(-limit);
  };

  listenerCount = (name) => {
    return this.#listeners.get(name)?.length || 0;
  };

  getActiveEvents = () => {
    const result = {};
    for (const [name, listeners] of this.#listeners) {
      if (listeners.length > 0) {
        result[name] = listeners.length;
      }
    }
    return result;
  };

  getLastValue = (name) => {
    return this.#lastValues.get(name);
  };

  /**
   * [v6.0.0] Получить Replay Buffer для события
   * @param {string} eventName 
   * @returns {Array}
   */
  getReplayBuffer = (eventName) => {
    return this.#replayBuffer.get(eventName) || [];
  };

  /**
   * [v6.0.0] Установить размер Replay Buffer
   * @param {number} size 
   * @returns {Result<boolean, string>}
   */
  setReplayBufferSize = (size) => {
    if (typeof size !== 'number' || size < 1 || size > 1000) {
      return Err('Size must be a number between 1 and 1000');
    }
    this.#replayBufferSize = size;
    return Ok(true);
  };

  getMetrics = () => ({
    ...this.#metrics,
    totalEventsCached: this.#lastValues.size,
    eventsInHistory: this.#eventTimestamps.size,
    activeListenerTypes: this.#listeners.size,
    totalListeners: Array.from(this.#listeners.values())
      .reduce((acc, arr) => acc + arr.length, 0),
    deadLetterQueueSize: this.#deadLetterQueue.length,
    registeredModules: this.#modules.size,
    replayBufferSize: this.#replayBufferSize,
    schemasRegistered: this.#schemas.size,
    config: { ...this.#config }
  });

  healthCheck = () => {
    const warnings = [];
    let totalListeners = 0;
    let maxListenersForEvent = 0;
    let maxEventName = '';

    for (const [name, listeners] of this.#listeners) {
      const count = listeners.length;
      totalListeners += count;
      if (count > maxListenersForEvent) {
        maxListenersForEvent = count;
        maxEventName = name;
      }
    }

    if (maxListenersForEvent > this.#config.maxListenersWarning) {
      warnings.push(`"${maxEventName}" has ${maxListenersForEvent} listeners (limit: ${this.#config.maxListenersWarning})`);
    }

    if (this.#metrics.recursiveBlocks > 0) {
      warnings.push(`${this.#metrics.recursiveBlocks} recursive MODULE_ERROR calls blocked`);
    }

    if (this.#eventTimestamps.size > 500) {
      warnings.push(`Event history is large: ${this.#eventTimestamps.size} entries`);
    }
    
    if (this.#deadLetterQueue.length > this.#dlqMaxSize * 0.8) {
      warnings.push(`DLQ is almost full: ${this.#deadLetterQueue.length}/${this.#dlqMaxSize}`);
    }
    
    if (this.#metrics.deadLetters > 100) {
      warnings.push(`High dead letter count: ${this.#metrics.deadLetters}`);
    }

    return {
      healthy: warnings.length === 0,
      warnings,
      totalListeners,
      activeEventTypes: this.#listeners.size,
      totalEventsEmitted: this.#metrics.emitted,
      totalErrors: this.#metrics.errors,
      deadLetters: this.#metrics.deadLetters,
      registeredModules: this.#modules.size,
    };
  };
}

// ═══════════════════════════════════════════════════════════════
//  ЭКСПОРТ (Синглтон)
// ═══════════════════════════════════════════════════════════════

const EventBusInstance = new EnterpriseEventBus();

// Все методы возвращают Result, поэтому bind безопасен
export const on = EventBusInstance.on.bind(EventBusInstance);
export const off = EventBusInstance.off.bind(EventBusInstance);
export const once = EventBusInstance.once.bind(EventBusInstance);
export const emit = EventBusInstance.emit.bind(EventBusInstance);
export const emitAsync = EventBusInstance.emitAsync.bind(EventBusInstance);
export const emitBatch = EventBusInstance.emitBatch.bind(EventBusInstance);
export const waitFor = EventBusInstance.waitFor.bind(EventBusInstance);
export const setDebug = EventBusInstance.setDebug.bind(EventBusInstance);
export const setConfig = EventBusInstance.setConfig.bind(EventBusInstance);
export const clearHistory = EventBusInstance.clearHistory.bind(EventBusInstance);
export const clearAllListeners = EventBusInstance.clearAllListeners.bind(EventBusInstance);
export const removeAllListeners = EventBusInstance.removeAllListeners.bind(EventBusInstance);
export const getMetrics = EventBusInstance.getMetrics.bind(EventBusInstance);
export const onThrottled = EventBusInstance.onThrottled.bind(EventBusInstance);
export const onDebounced = EventBusInstance.onDebounced.bind(EventBusInstance);
export const getEventLog = EventBusInstance.getEventLog.bind(EventBusInstance);
export const getLastValue = EventBusInstance.getLastValue.bind(EventBusInstance);
export const listenerCount = EventBusInstance.listenerCount.bind(EventBusInstance);
export const getActiveEvents = EventBusInstance.getActiveEvents.bind(EventBusInstance);
export const healthCheck = EventBusInstance.healthCheck.bind(EventBusInstance);

// [v6.0.0] Новые экспорты
export const registerModule = EventBusInstance.registerModule.bind(EventBusInstance);
export const disposeModule = EventBusInstance.disposeModule.bind(EventBusInstance);
export const getRegisteredModules = EventBusInstance.getRegisteredModules.bind(EventBusInstance);
export const getModuleListenerCount = EventBusInstance.getModuleListenerCount.bind(EventBusInstance);
export const registerSchema = EventBusInstance.registerSchema.bind(EventBusInstance);
export const unregisterSchema = EventBusInstance.unregisterSchema.bind(EventBusInstance);
export const getDeadLetterQueue = EventBusInstance.getDeadLetterQueue.bind(EventBusInstance);
export const clearDeadLetterQueue = EventBusInstance.clearDeadLetterQueue.bind(EventBusInstance);
export const replayDeadLetter = EventBusInstance.replayDeadLetter.bind(EventBusInstance);
export const getReplayBuffer = EventBusInstance.getReplayBuffer.bind(EventBusInstance);
export const setReplayBufferSize = EventBusInstance.setReplayBufferSize.bind(EventBusInstance);

// Доступ из консоли
if (typeof window !== 'undefined') {
  window.__EventBus = EventBusInstance;
  window.__EVENTS = EVENTS;
}

export default EventBusInstance;